import type { CSSProperties, ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  useProfileStore,
  getFacilityKeyForPortal,
  getPatientKey,
  type Patient,
} from "@/store/profile";
import { useTaskStore } from "@/store/task";
import { useAuth } from "@/hooks/useAuth";
import { topNavSteps } from "@/hooks/useWizardFlow";
import msgRequest from "@/api/msg-request";
import { cn } from "@/utils/cn";
import { navigateToFirstFormTask } from "@/utils/navigateToFormHandler";
import { Footer, type FooterProps } from "@/components/Footer";
import { PortalAccountMenu } from "@/components/PortalAccountMenu";
import {
  getVisibleProfileTabs,
  isProfilePath,
  profileTabFromLocation,
} from "@/config/profileNav";

const dashboardNavItems = [
  { to: "/", label: "Dashboard" },
  { to: "/menu/messages", label: "Messages" },
  { to: "/menu/documents", label: "Documents" },
  { to: "/menu/appointments", label: "Appointments" },
  { to: "/menu/profile", label: "Profile" },
];

const defaultComplaintNavItems = [
  { to: "/complaint/demographics", label: "Demographics" },
  { to: "/complaint/primary-complaint", label: "History" },
];

export interface PortalShellProps {
  children: ReactNode;
  /** Footer: when set, Contact Us opens this (e.g. modal) instead of navigating. */
  onContactClick?: () => void;
  /** Optional footer class (e.g. for fixed bottom nav spacing). */
  footerClassName?: FooterProps["className"];
  /** Optional: use a different wrapper class. */
  className?: string;
}

/**
 * Wrapper for portal pages: header (logo/facility name), context-aware nav,
 * user block, main content, and footer: the legal site footer on `/` and `/menu/*`
 * only. Other routes rely on each screen's wizard Back/Next row in layout flow.
 */
export function PortalShell({
  children,
  onContactClick,
  footerClassName,
  className,
}: PortalShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const profileAuthInfo = useProfileStore((s) => s.profileAuthInfo);
  const facilityInfo = useProfileStore((s) => s.facilityInfo);
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const patientAssociated = useProfileStore((s) => s.patientAssociated);
  const setCurrentPatient = useProfileStore((s) => s.setCurrentPatient);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const drawerCloseRef = useRef<HTMLButtonElement>(null);

  const pathname = location.pathname;
  const isComplaint = pathname.startsWith("/complaint");
  /** Site footer (Contact / Terms / Privacy): dashboard home and menu hub only. */
  const showSiteFooter =
    pathname === "/" || pathname.startsWith("/menu");
  /** iOS Safari: min-height-only shells let the page grow and scroll the whole column (including wizard actions). */
  const lockShellToViewport = pathname.startsWith("/complaint");
  const isAccountRoute = location.pathname.startsWith("/account");
  const showAuthControls = !isAccountRoute;
  const wizardSteps = useTaskStore((s) => s.wizardSteps);

  const complaintNavItems = useMemo(() => {
    const top = wizardSteps.length > 0 ? topNavSteps(wizardSteps) : [];
    const steps =
      top.length > 0
        ? top.map((s) => ({ to: s.path, label: s.label }))
        : defaultComplaintNavItems;
    // Let patients get back to the dashboard from inside a task (otherwise the task
    // header only shows wizard steps and there's no way out).
    return [{ to: "/", label: "Dashboard" }, ...steps];
  }, [wizardSteps]);

  const navItems = isComplaint ? complaintNavItems : dashboardNavItems;

  const canProcess = !!(facilityInfo?.openEdge || facilityInfo?.fortis);
  const mobileProfileSubItems = getVisibleProfileTabs(
    profileAuthInfo,
    canProcess
  );

  const officeName = currentPatient?.officeName ?? "Patient Portal";
  const accountLabel = profileAuthInfo
    ? `${profileAuthInfo.firstName} ${profileAuthInfo.lastName}`
    : "";
  const initials = accountLabel
    ? accountLabel
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "?";

  useEffect(() => {
    if (!currentPatient || isComplaint) return;
    const facilityKey = getFacilityKeyForPortal();
    const patientKey = getPatientKey();
    if (!facilityKey || !patientKey) return;
    let cancelled = false;
    msgRequest.request
      .getUnReadMessageCount(facilityKey, patientKey)
      .then((res) => {
        if (!cancelled && res.data != null) setUnreadCount(Number(res.data));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [currentPatient, isComplaint]);

  const handleLogout = () => {
    logout();
    useProfileStore.getState().logout();
    localStorage.removeItem("portal-profile");
    navigate("/account/login", { replace: true });
  };

  const handleSelectPatientFromMenu = (patient: Patient) => {
    setCurrentPatient(patient);
    navigate("/", { replace: true });
  };

  const getActiveForItem = (item: (typeof navItems)[0]) => {
    if (!isComplaint) {
      return item.to === "/"
        ? location.pathname === "/"
        : location.pathname === item.to;
    }
    const step = wizardSteps.find((s) => s.topNav && s.path === item.to);
    if (step) {
      const allPaths = [step.path, ...(step.childPaths ?? [])];
      return allPaths.some(
        (p) =>
          location.pathname === p ||
          location.pathname.startsWith(`${p}/`) ||
          location.pathname.startsWith(`${p}?`)
      );
    }
    return (
      location.pathname === item.to ||
      location.pathname.startsWith(`${item.to}/`)
    );
  };

  /* Restore the previous value: complaint routes keep body overflow locked (below). */
  useEffect(() => {
    if (!mobileNavOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  /** Drawer: focus the close button on open, keep Tab inside, Escape closes, focus returns to the hamburger. */
  useEffect(() => {
    if (!mobileNavOpen) return;
    const menuButton = menuButtonRef.current;
    drawerCloseRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileNavOpen(false);
        return;
      }
      if (e.key !== "Tab" || !drawerRef.current) return;
      const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
        "a[href], button:not([disabled])"
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      menuButton?.focus();
    };
  }, [mobileNavOpen]);

  /** Prevent document rubber-band scroll revealing empty layout below the vv-pinned shell. */
  useEffect(() => {
    if (!lockShellToViewport) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, [lockShellToViewport]);

  useEffect(() => {
    const root = document.documentElement;
    const updateViewportVar = () => {
      const vv = window.visualViewport;
      const viewportHeight = vv?.height ?? window.innerHeight;
      root.style.setProperty("--portal-vh", `${Math.round(viewportHeight)}px`);
      /*
       * iOS Safari: when the keyboard opens, the browser scrolls the *layout* viewport
       * (visualViewport.offsetTop > 0) while our shell was still anchored at y=0.
       * That leaves a tall empty band between the form and the keyboard. Pin the
       * complaint shell with top: offsetTop + height: visual height so it tracks
       * the visible rectangle.
       */
      if (vv) {
        root.style.setProperty(
          "--portal-vv-offset-top",
          `${Math.round(vv.offsetTop)}px`
        );
      } else {
        root.style.setProperty("--portal-vv-offset-top", "0px");
      }
    };

    updateViewportVar();
    window.addEventListener("resize", updateViewportVar);
    window.addEventListener("orientationchange", updateViewportVar);
    window.visualViewport?.addEventListener("resize", updateViewportVar);
    window.visualViewport?.addEventListener("scroll", updateViewportVar);
    /* Keyboard focus can lag visualViewport resize on iOS; refresh in the same frame chain. */
    document.addEventListener("focusin", updateViewportVar, true);
    document.addEventListener("focusout", updateViewportVar, true);

    return () => {
      window.removeEventListener("resize", updateViewportVar);
      window.removeEventListener("orientationchange", updateViewportVar);
      window.visualViewport?.removeEventListener("resize", updateViewportVar);
      window.visualViewport?.removeEventListener("scroll", updateViewportVar);
      document.removeEventListener("focusin", updateViewportVar, true);
      document.removeEventListener("focusout", updateViewportVar, true);
    };
  }, []);

  const shellVh = showSiteFooter
    ? "var(--portal-vh, 100dvh)"
    : "var(--portal-vh, 100svh)";
  const shellStyle: CSSProperties = {
    minHeight: shellVh,
  };
  if (lockShellToViewport) {
    shellStyle.height = shellVh;
    shellStyle.maxHeight = shellVh;
    /* Track iOS visual viewport after keyboard / chrome scroll (offsetTop). */
    shellStyle.position = "fixed";
    shellStyle.left = 0;
    shellStyle.right = 0;
    shellStyle.top = "var(--portal-vv-offset-top, 0px)";
    shellStyle.width = "100%";
    shellStyle.maxWidth = "100%";
    shellStyle.zIndex = 2;
    shellStyle.overflowY = "hidden";
  }

  return (
    <div
      className={cn(
        /* overflow-y: visible for dashboard; complaint shell uses inline overflowY hidden */
        "inner-wrapper font-nunito flex w-full min-w-0 max-w-full flex-col overflow-x-clip bg-healthsource-lightGray",
        lockShellToViewport ? "min-h-0 overflow-y-hidden" : "overflow-y-visible",
        /* Room for the site footer, which is fixed only from md up (in flow on phones). */
        showSiteFooter && "md:pb-[max(3.5rem,env(safe-area-inset-bottom,0px))]",
        className
      )}
      style={shellStyle}
    >
      <header
        className="sticky top-0 z-50 w-full max-w-full min-w-0 overflow-x-clip border-b border-white/10 bg-healthsource-chrome pt-[env(safe-area-inset-top,0px)] shadow-lg"
        role="banner"
        aria-label="Site header"
      >
        {/* Mobile header: keep left/right safe-area padding symmetric to avoid right-shift on iOS. */}
        <div
          className="grid h-16 w-full max-w-full min-w-0 flex-shrink-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-1 sm:gap-2 sm:px-4 md:hidden"
          style={{
            paddingLeft:
              "max(0.75rem, env(safe-area-inset-left, 0px), env(safe-area-inset-right, 0px))",
            paddingRight:
              "max(0.75rem, env(safe-area-inset-left, 0px), env(safe-area-inset-right, 0px))",
          }}
        >
        <button
          ref={menuButtonRef}
          type="button"
          onClick={() => setMobileNavOpen(true)}
          className="relative flex h-11 w-11 shrink-0 items-center justify-self-start justify-center rounded text-white hover:bg-white/10"
          aria-label={
            !isComplaint && unreadCount > 0
              ? `Open navigation menu, ${unreadCount} unread messages`
              : "Open navigation menu"
          }
          aria-expanded={mobileNavOpen}
          aria-controls="portal-mobile-nav"
        >
          <i className="fas fa-bars text-lg" aria-hidden />
          {/* Unread messages live in the drawer; flag them on the closed hamburger too. */}
          {!isComplaint && unreadCount > 0 && (
            <span
              className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full bg-healthsource-red ring-2 ring-healthsource-chrome"
              aria-hidden
            />
          )}
        </button>
        {/* Long office names wrap to two lines at a legible size rather than shrinking or cutting off. */}
        <div className="min-w-0 justify-self-center px-1 text-center text-[15px] font-bold leading-tight text-white">
          <span className="line-clamp-2 break-words" title={officeName}>
            {officeName}
          </span>
        </div>
        {showAuthControls && (
          <div className="flex min-w-0 shrink-0 items-center justify-self-end">
            {accountLabel && (
              <PortalAccountMenu
                onSignOut={handleLogout}
                patients={patientAssociated ?? null}
                currentPatient={currentPatient}
                onPatientSelect={handleSelectPatientFromMenu}
                initials={initials}
              />
            )}
          </div>
        )}
        </div>

        {/* Desktop header bar */}
        <div className="hidden h-16 flex-shrink-0 items-center gap-2 px-4 sm:px-6 md:flex">
        <div className="flex min-w-0 flex-1 items-center">
          <span
            className="truncate text-base font-bold text-white"
            title={officeName}
          >
            {officeName}
          </span>
        </div>

        <nav
          className="flex min-w-0 justify-center gap-1 overflow-x-auto"
          role="navigation"
          aria-label="Main"
        >
          {navItems.map((item) => {
            const isActive = getActiveForItem(item);
            const navClass = cn(
              "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded px-2.5 py-1.5 text-sm font-bold transition-colors lg:px-4",
              isActive
                ? "border-b-2 border-healthsource-chromeAccent text-white"
                : "border-b-2 border-transparent text-white/55 hover:text-white"
            );

            if (isComplaint && item.to === "/form-handler") {
              return (
                <button
                  key={item.to}
                  type="button"
                  className={navClass}
                  onClick={() => {
                    void navigateToFirstFormTask(navigate).then((ok) => {
                      if (!ok) {
                        toast.error("No forms to complete right now.");
                      }
                    });
                  }}
                >
                  {item.label}
                </button>
              );
            }

            return (
              <Link key={item.to} to={item.to} className={navClass}>
                {isComplaint && item.to === "/" && (
                  <i className="fas fa-home" aria-hidden />
                )}
                {item.label}
                {!isComplaint &&
                  item.label === "Messages" &&
                  unreadCount > 0 && (
                    <span className="min-w-[18px] rounded-full bg-healthsource-red px-1.5 py-0.5 text-center text-[10px] font-extrabold text-white">
                      {unreadCount}
                    </span>
                  )}
              </Link>
            );
          })}
        </nav>

        <div className="flex min-w-0 flex-1 items-center justify-end gap-2 sm:gap-3">
          {showAuthControls && accountLabel && (
            <PortalAccountMenu
              onSignOut={handleLogout}
              patients={patientAssociated ?? null}
              currentPatient={currentPatient}
              onPatientSelect={handleSelectPatientFromMenu}
              initials={initials}
            />
          )}
        </div>
        </div>
      </header>

      {/* Mobile slide-out (outside header for correct fixed stacking) */}
      {showAuthControls && mobileNavOpen && (
        <div
          id="portal-mobile-nav"
          className="fixed inset-0 z-[60] md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
        >
          {/* Backdrop is pointer-only; keyboard users close with Escape or the close button. */}
          <div
            className="absolute inset-0 z-0 bg-black/50 duration-200 animate-in fade-in motion-reduce:animate-none"
            aria-hidden
            onClick={() => setMobileNavOpen(false)}
          />
          <div
            ref={drawerRef}
            className="absolute left-0 top-0 z-10 flex h-full w-[min(20rem,88vw)] flex-col bg-healthsource-chrome pb-[env(safe-area-inset-bottom,0px)] pl-[env(safe-area-inset-left,0px)] pt-[env(safe-area-inset-top,0px)] shadow-2xl duration-200 animate-in slide-in-from-left-full motion-reduce:animate-none"
          >
            {/* Same height as the app header so the close button sits where the hamburger was. */}
            <div className="flex h-16 shrink-0 items-center justify-between gap-2 border-b border-white/10 pl-4 pr-3">
              <span className="truncate text-[15px] font-bold text-white" title={officeName}>
                {officeName}
              </span>
              <button
                ref={drawerCloseRef}
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded text-white hover:bg-white/10"
                aria-label="Close navigation"
              >
                <i className="fas fa-times text-lg" aria-hidden />
              </button>
            </div>
            <nav className="flex-1 space-y-1 overflow-y-auto overscroll-contain p-3" aria-label="Main">
              {navItems.map((item) => {
                if (!isComplaint && item.to === "/menu/profile") {
                  const anyProfileActive = isProfilePath(location.pathname);
                  return (
                    <div key="profile" className="mb-1 space-y-0.5">
                      <p
                        className="px-3 pb-0.5 pt-1.5 text-[10px] font-extrabold uppercase tracking-wider text-white/45"
                        id="nav-profile-group-label"
                      >
                        Profile
                      </p>
                      <ul
                        className="ml-0.5 space-y-0.5 border-l border-white/15 pl-2.5"
                        aria-labelledby="nav-profile-group-label"
                      >
                        {mobileProfileSubItems.map((tab) => {
                          const subActive =
                            anyProfileActive &&
                            profileTabFromLocation(location.search) ===
                              tab.id;
                          return (
                            <li key={tab.id}>
                              <Link
                                to={`/menu/profile?page=${tab.id}`}
                                className={cn(
                                  "flex min-h-11 w-full items-center rounded px-2.5 py-2 text-left text-[15px] font-bold leading-snug",
                                  subActive
                                    ? "bg-white/10 text-white"
                                    : "text-white/75 hover:bg-white/10"
                                )}
                                onClick={() => setMobileNavOpen(false)}
                              >
                                {tab.label}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                }
                const isActive = getActiveForItem(item);
                const itemClass = cn(
                  "flex min-h-11 w-full items-center rounded px-3 py-2 text-left text-base font-bold",
                  isActive
                    ? "bg-white/10 text-white"
                    : "text-white/80 hover:bg-white/10"
                );
                if (isComplaint && item.to === "/form-handler") {
                  return (
                    <button
                      key={item.to}
                      type="button"
                      className={itemClass}
                      onClick={() => {
                        setMobileNavOpen(false);
                        void navigateToFirstFormTask(navigate).then((ok) => {
                          if (!ok) toast.error("No forms to complete right now.");
                        });
                      }}
                    >
                      {item.label}
                    </button>
                  );
                }
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={itemClass}
                    onClick={() => setMobileNavOpen(false)}
                  >
                    {isComplaint && item.to === "/" && (
                      <i className="fas fa-home mr-2 shrink-0" aria-hidden />
                    )}
                    {item.label}
                    {!isComplaint &&
                      item.label === "Messages" &&
                      unreadCount > 0 && (
                        <span className="ml-auto min-w-[20px] rounded-full bg-healthsource-red px-1.5 py-0.5 text-center text-xs font-extrabold text-white">
                          {unreadCount}
                        </span>
                      )}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {children}

      {showSiteFooter ? (
        <Footer
          className={footerClassName}
          onContactClick={onContactClick}
        />
      ) : null}
    </div>
  );
}
